import { ORG_ROLE, OrgRole, WORKSPACE_ROLE, WorkspaceRole } from "@/types/user";

// Route permission definition
type RoutePermission = {
  pattern: string;
  // Org roles that can access this route (org:admin always has access, so only specify if org:member can too)
  orgMemberAllowed: boolean;
  // Workspace roles that can access this route
  allowedWorkspaceRoles: WorkspaceRole[];
};

/**
 * Route permissions configuration
 *
 * Rules:
 * - org:admin can access everything
 * - org:member can only access routes where orgMemberAllowed=true AND they have a workspace role in allowedWorkspaceRoles
 * - Workspace roles are checked for both org:member and workspace-specific access
 */
export const ROUTE_PERMISSIONS: RoutePermission[] = [
  // Dashboard - org:admin, ws:admin, ws:member, ws:agent
  // Agents get a limited dashboard showing only the "My Activities" section
  {
    pattern: "/dashboard",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [
      WORKSPACE_ROLE.ADMIN,
      WORKSPACE_ROLE.MEMBER,
      WORKSPACE_ROLE.AGENT,
    ],
  },

  // Inbox - everyone can access
  {
    pattern: "/inbox",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [
      WORKSPACE_ROLE.ADMIN,
      WORKSPACE_ROLE.MEMBER,
      WORKSPACE_ROLE.AGENT,
    ],
  },

  // Profile - everyone can access
  {
    pattern: "/profile",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [
      WORKSPACE_ROLE.ADMIN,
      WORKSPACE_ROLE.MEMBER,
      WORKSPACE_ROLE.AGENT,
    ],
  },

  // Contacts - org:admin, ws:admin, ws:member, ws:agent
  {
    pattern: "/contacts",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [
      WORKSPACE_ROLE.ADMIN,
      WORKSPACE_ROLE.MEMBER,
      WORKSPACE_ROLE.AGENT,
    ],
  },

  // Tickets - org:admin, ws:admin, ws:member
  {
    pattern: "/tickets",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },

  // Broadcasts - org:admin, ws:admin, ws:member
  {
    pattern: "/broadcasts",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },

  // Ads - org:admin, ws:admin, ws:member
  {
    pattern: "/ads",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },

  // Channels - org:admin, ws:admin, ws:member
  {
    pattern: "/channels",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },

  // Reports - org:admin, ws:admin, ws:member
  {
    pattern: "/reports",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },

  // Integrations - org:admin, ws:admin, ws:member
  {
    pattern: "/integrations",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },

  // Ecommerce - org:admin, ws:admin, ws:member
  {
    pattern: "/ecommerce",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },

  // Clinic routes - org:admin, ws:admin, ws:member
  {
    pattern: "/treatments",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },
  {
    pattern: "/doctors",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },
  {
    pattern: "/appointments",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [
      WORKSPACE_ROLE.ADMIN,
      WORKSPACE_ROLE.MEMBER,
      WORKSPACE_ROLE.AGENT,
    ],
  },

  // Settings - Organization level (org:admin only)
  {
    pattern: "/settings/organization",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [],
  },
  {
    pattern: "/settings/users",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [],
  },
  {
    pattern: "/settings/developer",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN],
  },

  // Settings - Workspace level (org:admin, ws:admin)
  {
    pattern: "/settings/workspace",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN],
  },
  {
    pattern: "/settings/workspace-users",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN],
  },
  {
    pattern: "/settings/teams",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },

  // Sophus (AI agent) - org:admin, ws:admin, ws:member
  {
    pattern: "/sophus",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },

  // Business Hours - org:admin, ws:admin, ws:member
  {
    pattern: "/settings/business-hours",
    orgMemberAllowed: true,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },

  // Settings - Workspace content (org:admin, ws:admin, ws:member)
  // Snippets and Files are also viewable by ws:agent so agents can reference
  // saved replies and files while handling conversations.
  {
    pattern: "/settings/snippets",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [
      WORKSPACE_ROLE.ADMIN,
      WORKSPACE_ROLE.MEMBER,
      WORKSPACE_ROLE.AGENT,
    ],
  },
  {
    pattern: "/settings/files",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [
      WORKSPACE_ROLE.ADMIN,
      WORKSPACE_ROLE.MEMBER,
      WORKSPACE_ROLE.AGENT,
    ],
  },
  {
    pattern: "/settings/tags",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },
  {
    pattern: "/settings/contact-sources",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },
  {
    pattern: "/settings/closing-notes",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },
  {
    pattern: "/settings/wrap-up",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },
  {
    pattern: "/settings/lifecycle",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },

  // Settings index - will be handled separately for redirect
  {
    pattern: "/settings",
    orgMemberAllowed: false,
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
  },
];

/**
 * Find the matching route permission for a given path
 */
function findRoutePermission(path: string): RoutePermission | undefined {
  // Normalize path - remove trailing slash
  const normalizedPath = path.endsWith("/") ? path.slice(0, -1) : path;

  // Find exact match or prefix match (for nested routes like /inbox/123/456)
  return ROUTE_PERMISSIONS.find((permission) => {
    // Exact match
    if (normalizedPath === permission.pattern) {
      return true;
    }
    // Prefix match for nested routes (e.g., /inbox/ws-123 matches /inbox)
    if (normalizedPath.startsWith(permission.pattern + "/")) {
      return true;
    }

    return false;
  });
}

/**
 * Check if a user can access a given route based on their roles
 */
export function canAccessRoute(
  path: string,
  orgRole: OrgRole | null,
  workspaceRole: WorkspaceRole | null,
): boolean {
  // No role means no access
  if (!orgRole) {
    return false;
  }

  // org:admin can access everything
  if (orgRole === ORG_ROLE.ADMIN) {
    return true;
  }

  // Find the route permission
  const permission = findRoutePermission(path);

  // If no permission found, default to deny for org:member
  if (!permission) {
    return false;
  }

  // For org:member, check if they're allowed and have the right workspace role
  if (orgRole === ORG_ROLE.MEMBER) {
    // Check if org:member is allowed for this route
    if (!permission.orgMemberAllowed) {
      // Even if orgMemberAllowed is false, workspace roles might still grant access
      // This is for routes like settings that require specific workspace roles
      if (!workspaceRole) {
        return false;
      }

      return permission.allowedWorkspaceRoles.includes(workspaceRole);
    }

    // org:member is allowed, but still need to check workspace role
    if (!workspaceRole) {
      return false;
    }

    return permission.allowedWorkspaceRoles.includes(workspaceRole);
  }

  return false;
}

/**
 * Get the fallback route for a user when they try to access an unauthorized route
 */
export function getFallbackRoute(
  orgRole: OrgRole | null,
  workspaceRole: WorkspaceRole | null,
  activeWorkspaceId: string | null,
): string {
  // "My Inbox" is the home for anyone with a workspace — conversations stay
  // visible and the My Activities cards live in the inbox landing.
  if (activeWorkspaceId) {
    return `/inbox/${activeWorkspaceId}`;
  }

  // No workspace (e.g. an org admin not in any workspace): fall back to the
  // reports.
  return "/reports/contacts";
}

/**
 * Get the default settings route based on user role
 */
export function getSettingsDefaultRoute(
  orgRole: OrgRole | null,
  workspaceRole: WorkspaceRole | null,
): string {
  // org:admin goes to organization settings
  if (orgRole === ORG_ROLE.ADMIN) {
    return "/settings/organization";
  }

  // ws:admin goes to workspace settings
  if (workspaceRole === WORKSPACE_ROLE.ADMIN) {
    return "/settings/workspace";
  }

  // ws:member goes to snippets
  if (workspaceRole === WORKSPACE_ROLE.MEMBER) {
    return "/settings/snippets";
  }

  // Fallback (should not happen for authorized users)
  return "/settings/snippets";
}
